/**
 * Runtime CSS for editable <img> positioning + hover presets.
 *
 * Mirrors logo-normalizer.js's withLogoRuntimeCSS() pattern but targets any
 * editable image gated by the `data-image-edit` engaged attribute (and the
 * optional `data-image-hover` preset attribute). Engaged-attribute names
 * intentionally omit the `pb-` prefix so stripAllBuilderAttrs() leaves them
 * intact during export.
 *
 * Images without `data-image-edit` get zero rules from this block — they
 * render exactly as before, so no collisions with CodeStitch transforms.
 */

const IMAGE_RUNTIME_MARKER = 'pb-image-runtime-v1';

export const IMAGE_RUNTIME_CSS = `/* ${IMAGE_RUNTIME_MARKER} */
img[data-image-edit]{--pb-image-tx-pct:0%;--pb-image-ty-pct:0%;--pb-image-scale:1;--pb-image-hover-scale:1;--pb-image-hover-y:0rem;--pb-image-hover-rotate:0deg;--pb-image-static-filter:brightness(1);--pb-image-hover-filter:brightness(1);--pb-image-hover-opacity:1;transform-origin:center center;transform:translate(var(--pb-image-tx-pct,0%),var(--pb-image-ty-pct,0%)) translateY(var(--pb-image-hover-y,0rem)) rotate(var(--pb-image-hover-rotate,0deg)) scale(var(--pb-image-scale,1)) scale(var(--pb-image-hover-scale,1));filter:var(--pb-image-static-filter,brightness(1)) var(--pb-image-hover-filter,brightness(1));opacity:var(--pb-image-hover-opacity,1);transition:transform .25s ease,filter .25s ease,opacity .25s ease}
img[data-image-dragging]{transition:none!important}
img[data-image-effect="dim"]{--pb-image-static-filter:brightness(.6)}
img[data-image-effect="darken"]{--pb-image-static-filter:brightness(.42)}
img[data-image-effect="grayscale"]{--pb-image-static-filter:grayscale(1)}
img[data-image-effect="warm"]{--pb-image-static-filter:saturate(1.25) sepia(.35) hue-rotate(-10deg)}
img[data-image-effect="cool"]{--pb-image-static-filter:saturate(1.15) hue-rotate(12deg) brightness(1.02)}
img[data-image-hover="scale"]:hover{--pb-image-hover-scale:1.05}
img[data-image-hover="lift"]:hover{--pb-image-hover-y:-.25rem;--pb-image-hover-filter:drop-shadow(0 .5rem .75rem rgba(0,0,0,.18))}
img[data-image-hover="rotate"]:hover{--pb-image-hover-rotate:-2deg}
img[data-image-hover="shimmer"]:hover{animation:pb-image-shimmer .8s ease-in-out}
img[data-image-hover="color-shift"]:hover{--pb-image-hover-filter:hue-rotate(24deg)}
@keyframes pb-image-shimmer{0%,100%{opacity:1}50%{opacity:.68}}
@media (prefers-reduced-motion:reduce){img[data-image-edit],img[data-image-hover]:hover{--pb-image-hover-scale:1;--pb-image-hover-y:0rem;--pb-image-hover-rotate:0deg;--pb-image-hover-filter:brightness(1)}img[data-image-edit]{animation:none!important;transition:none!important}}`;

export function withImageRuntimeCSS(css = '') {
  const rawCSS = typeof css === 'string' ? css : '';
  if (rawCSS.includes(IMAGE_RUNTIME_MARKER)) return rawCSS;
  const baseCSS = rawCSS.trimEnd();
  return [baseCSS, IMAGE_RUNTIME_CSS].filter(Boolean).join('\n\n');
}

/**
 * Allowed values for the imageHover override and the data-image-hover attr.
 * Validated at both the panel call site and the bridge handler.
 */
export const IMAGE_HOVER_PRESETS = Object.freeze([
  'none',
  'scale',
  'lift',
  'rotate',
  'shimmer',
  'color-shift'
]);

export function isValidImageHoverPreset(value) {
  return IMAGE_HOVER_PRESETS.includes(String(value || ''));
}

/**
 * Allowed values for the imageEffect override and the data-image-effect attr.
 * Static tone/dim filters (unlike hover presets, they apply at rest) —
 * validated at both the panel call site and the bridge handler. 'none' clears.
 */
export const IMAGE_EFFECT_PRESETS = Object.freeze([
  'none',
  'dim',
  'darken',
  'grayscale',
  'warm',
  'cool'
]);

export function isValidImageEffect(value) {
  return IMAGE_EFFECT_PRESETS.includes(String(value || ''));
}

export function hasImageEffectOverride(props) {
  if (!props || typeof props !== 'object') return false;
  const v = props.imageEffect;
  return typeof v === 'string' && v !== '' && v !== 'none' && isValidImageEffect(v);
}

/**
 * Returns true if any of the image-position/hover overrides on a single eid
 * are non-default. Defaults (imageTx=0, imageTy=0, imageScale=1,
 * imageHover='none' or missing) should never persist; the panel handlers
 * clear them. This helper exists for HTML-assembly and export code to
 * decide whether to set the `data-image-edit` engaged attribute.
 */
export function hasImagePositionOverride(props) {
  if (!props || typeof props !== 'object') return false;
  const tx = Number(props.imageTx);
  const ty = Number(props.imageTy);
  const scale = Number(props.imageScale);
  if (Number.isFinite(tx) && tx !== 0) return true;
  if (Number.isFinite(ty) && ty !== 0) return true;
  if (Number.isFinite(scale) && scale !== 1) return true;
  return false;
}

export function hasImageHoverOverride(props) {
  if (!props || typeof props !== 'object') return false;
  const v = props.imageHover;
  return typeof v === 'string' && v !== '' && v !== 'none' && isValidImageHoverPreset(v);
}

/**
 * Format a numeric fraction (-1..1) as a CSS percentage string with one
 * decimal of precision (e.g. 0.0537 → "5.4%"). Used by both css-assembler
 * (preview CSS vars) and generators (export inline style).
 */
export function formatImagePct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  const pct = n * 100;
  if (Math.abs(pct - Math.round(pct)) < 0.05) return `${Math.round(pct)}%`;
  return `${pct.toFixed(1)}%`;
}

export function formatImageScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '1';
  if (Math.abs(n - Math.round(n)) < 0.005) return String(Math.round(n));
  return n.toFixed(3).replace(/\.?0+$/, '');
}
